/**
 * ERPActionColumn — Dropdown via React Portal (FINAL)
 * 
 * RULE: ALL dropdowns MUST use portal rendering.
 * NEVER render inside scrollable containers.
 * z-index alone is NOT the solution — portal IS.
 * 
 * z-index layers: dropdown=1000, modal=1100, toast=1200
 * 
 * GLOBAL COMPONENT — used by ALL grids (main + embedded).
 * DO NOT recreate per screen.
 */
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Eye, Edit2, Trash2, FileText, Printer, Download, Mail, MoreVertical, Clock, BookOpen } from 'lucide-react';

const ERPActionColumn = ({ row, onView, onEdit, onVoid, onHistory, onLedger, onPreview, onPrint, onExport, onEmail }) => {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const [voidConfirm, setVoidConfirm] = useState(false);
  const triggerRef = useRef(null);

  const actions = [
    onView    && { icon: Eye,      label: 'View',         handler: onView,    cls: 'act-view' },
    onEdit    && { icon: Edit2,    label: 'Edit',         handler: onEdit,    cls: 'act-edit' },
    onVoid    && { icon: Trash2,   label: 'Void',         handler: onVoid,    cls: 'act-delete' },
    onHistory && { icon: Clock,    label: 'Acct History', handler: onHistory, cls: 'act-history' },
    onLedger  && { icon: BookOpen, label: 'Open Ledger',  handler: onLedger,  cls: 'act-ledger' },
    onPreview && { icon: FileText, label: 'Preview',      handler: onPreview, cls: 'act-preview' },
    onPrint   && { icon: Printer,  label: 'Print',        handler: onPrint,   cls: 'act-print' },
    onExport  && { icon: Download, label: 'Export',       handler: onExport,  cls: 'act-export' },
    onEmail   && { icon: Mail,     label: 'Email',        handler: onEmail,   cls: 'act-email' },
  ].filter(Boolean);

  const calcPos = useCallback(() => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const dropH = actions.length * 36 + 12;
    const dropW = 170;
    const belowSpace = window.innerHeight - rect.bottom;
    const rightSpace = window.innerWidth - rect.right;
    const top = belowSpace < dropH ? rect.top - dropH : rect.bottom + 4;
    const left = rightSpace >= dropW ? rect.right + 4 : rect.left - dropW - 4;
    setPos({ top: Math.max(4, top), left: Math.max(4, left) });
  }, [actions.length]);

  const handleToggle = useCallback(() => {
    if (!open) calcPos();
    setOpen(o => !o);
  }, [open, calcPos]);

  // Close on outside click, scroll, resize
  useEffect(() => {
    if (!open) return;
    const close = () => setOpen(false);
    const onClickOutside = (e) => {
      if (triggerRef.current && !triggerRef.current.contains(e.target)) close();
    };
    document.addEventListener('mousedown', onClickOutside);
    document.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    return () => {
      document.removeEventListener('mousedown', onClickOutside);
      document.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
    };
  }, [open]);

  // Portal dropdown — rendered at document.body, outside ALL containers
  const dropdown = open ? createPortal(
    <div className="erp-action-dropdown" style={{ top: pos.top, left: pos.left }}
      data-testid="action-dropdown" onMouseDown={(e) => e.stopPropagation()}>
      {!voidConfirm ? (
        actions.map(a => (
          <button key={a.label} className={`erp-action-item ${a.cls}`}
            onClick={() => {
              if (a.cls === 'act-delete') { setVoidConfirm(true); return; }
              a.handler(row); setOpen(false);
            }}
            data-testid={`action-${a.label.toLowerCase().replace(/ /g,'-')}-${row?.CatId || row?.DocId || row?.id}`}>
            <a.icon size={14} /><span>{a.label}</span>
          </button>
        ))
      ) : (
        <div style={{ padding: '10px 12px', minWidth: 190 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: '#dc2626', marginBottom: 6, display: 'flex', alignItems: 'center', gap: 6 }}>
            <Trash2 size={13} /> Void this record?
          </div>
          <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 10 }}>
            This action marks the entry as void.<br />It cannot be undone.
          </div>
          <div style={{ display: 'flex', gap: 6 }}>
            <button
              data-testid="void-confirm-yes-btn"
              onClick={() => { if (onVoid) onVoid(row); setVoidConfirm(false); setOpen(false); }}
              style={{ flex: 1, padding: '4px 10px', background: '#dc2626', color: '#fff', border: 'none', borderRadius: 4, fontSize: 11, fontWeight: 700, cursor: 'pointer' }}
            >Yes, Void</button>
            <button
              data-testid="void-confirm-cancel-btn"
              onClick={() => setVoidConfirm(false)}
              style={{ flex: 1, padding: '4px 10px', background: '#f3f4f6', color: '#374151', border: '1px solid #d1d5db', borderRadius: 4, fontSize: 11, cursor: 'pointer' }}
            >Cancel</button>
          </div>
        </div>
      )}
    </div>,
    document.body
  ) : null;

  return (
    <div className="erp-action-wrapper" ref={triggerRef} onClick={(e) => e.stopPropagation()}>
      <button className="erp-action-trigger" onClick={handleToggle}
        data-testid={`action-toggle-${row?.CatId || row?.DocId || row?.id || 'row'}`}>
        <MoreVertical size={15} />
      </button>
      {dropdown}
    </div>
  );
};

export default ERPActionColumn;
